import { useState } from "react";
import Like from "./Display/Like";
import Comments from "./Comments";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import MainCard from "./Layouts/MainCard";
import { Theme } from "../data/style/Theme";
import * as routes from "../data/links/routes";
import useDrawer from "../context/DrawerContext";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "../context/AuthContext";
import { getWorkoutById } from "../api/workouts";
import { errorTexts } from "../data/assets/texts";
import { useMutation } from "@tanstack/react-query";
import useScreenSize from "../context/ScreenContext";
import ExerciseWeight from "./Display/ExerciseWeight";
import { useQueryClient } from "@tanstack/react-query";
import { deleteWod, shareWorkout } from "../api/workouts";
import { ConfirmActionModal } from "./Modals/ConfirmActionModal";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useNotifications } from "../context/NotificationContext";
import WorkoutLeaderBoard from "../components/WorkoutLeaderboard";
// import ChallengeAthleteModal from "./Modals/ChallengeAthleteModal";
import { LEVELOPTIONS, WORKOUT_TYPES_DATA } from "../data/assets/arrays";
import {
  CenteredRow,
  MainTitle,
  AllCenteredColumn,
  SpacedRow,
  scrollBar,
  MainText,
  SecondaryTitle,
} from "../data/style/Style";

const MILE_CONVERT = 0.000621371192;

const WodDisplay = (props) => {
  let { wodId, setWorkouts } = props;
  const navigate = useNavigate();
  const client = useQueryClient();
  const { mobileDevice } = useScreenSize();

  const { pathname } = useLocation();
  const { profile, settings, user } = useAuth();
  const [confirmModalActive, setConfirmModalActive] = useState(false);
  let { enableNotification } = useNotifications();
  const { setContent, toggleDrawer } = useDrawer();
  const {
    data: workout,
    isPending: isWorkoutPending,
    refetch: getWorkout,
  } = useQuery({
    queryKey: ["workout", parseInt(wodId)],
    queryFn: ({ signal }) => getWorkoutById({ signal, wodId }),
  });

  const { mutate: share } = useMutation({
    mutationFn: shareWorkout,
    onSuccess: (response) => {
      getWorkout();
      client.invalidateQueries(["publicWods"]);
      enableNotification(response.data.message, "success");
    },
    onError: (error) => {
      if (error.response.status === 400) {
        enableNotification(error.response.data.message, "error");
        return;
      }

      if (error.response.status === 403) {
        navigate(routes.LOGIN);
        return;
      }

      if (error.response.status === 404) {
        enableNotification(error.response.data.message, "info");
        return;
      }

      if (error.response.status >= 500) {
        enableNotification(errorTexts.system_error, "error");
      }
    },
  });

  const { mutate: deleteWorkout } = useMutation({
    mutationFn: deleteWod,
    onSuccess: (response) => {
      enableNotification(response.data.message, "success");
      setConfirmModalActive(false);
      if (pathname === routes.USER_WORKOUTS) {
        setWorkouts((prev) => prev.filter((item) => item !== wodId));
      }
      navigate(routes.USER_WORKOUTS);
    },
    onError: (error) => {
      if (error.response.status === 420) {
        enableNotification(error.response.data.message, "info");
        return;
      }

      if (error.response.status === 400) {
        enableNotification(error.response.data.message, "error");
        return;
      }

      if (error.response.status === 403) {
        navigate(routes.LOGIN);
        return;
      }

      if (error.response.status >= 500) {
        enableNotification(errorTexts.system_error, "error");
        return;
      }
    },
  });

  let handleDeleteWorkout = () => deleteWorkout({ wodId: workout.id });

  let handleShareWorkout = () => share({ wodId: workout.id });

  const icon =
    !isWorkoutPending &&
    WORKOUT_TYPES_DATA.find((item) => item.id === workout.type).icon;

  const levelColor =
    !isWorkoutPending &&
    LEVELOPTIONS.find((item) => item.id === workout.level_info.level).color;

  return (
    <MainCard start={levelColor} middle={levelColor}>
      {!isWorkoutPending && (
        <>
          <Link
            style={{ textDecoration: "none" }}
            to={routes.SHOW_WORKOUT + workout.id}
          >
            <Stack
              height={!mobileDevice && "45vh"}
              minHeight={!mobileDevice && "310px"}
            >
              <SpacedRow
                width={"100%"}
                alignItems={"center"}
                sx={{
                  borderBottom: 1,
                  borderColor: Theme.palette.borderLight,
                  pb: 1,
                }}
              >
                <CenteredRow gap={1}>
                  <MainText fontSize={"1.2rem"}>{icon}</MainText>
                  <Stack>
                    <MainTitle>{workout.type_name}</MainTitle>
                    <MainText
                      fontSize={"0.8rem"}
                      sx={{ color: Theme.palette.muted + " !important" }}
                    >
                      {workout.type !== 4 ? (
                        `Timecap: ${workout.timecap_min} minutes`
                      ) : (
                        <>
                          {workout.rest !== 0
                            ? `${workout.rounds} rounds: ${
                                workout.every
                              } minutes on / ${workout.rest} ${
                                workout.rest < 2 ? "minute" : "minutes"
                              } off`
                            : `Every ${workout.every} minutes for ${workout.timecap_min} minutes`}
                        </>
                      )}
                    </MainText>
                    {(workout.type === 2 || workout.type === 5) && (
                      <MainText
                        fontSize={"0.8rem"}
                        sx={{ color: Theme.palette.muted + " !important" }}
                      >
                        Target score:{" "}
                        {profile.gender === 1
                          ? workout.female_projected_score
                          : workout.projected_score}{" "}
                        reps
                      </MainText>
                    )}
                  </Stack>
                </CenteredRow>
                <Stack gap={1}>
                  <span
                    style={{
                      fontFamily: "DM Sans, sans-serif",
                      fontWeight: 700,
                      fontSize: 13,
                      color: levelColor,
                      padding: "2px 10px",
                      borderRadius: "12px",
                      background: `${levelColor}14`,
                      border: `1px solid ${levelColor}30`,
                      textAlign: "center",
                    }}
                  >
                    {workout.level_info.label}
                  </span>
                  <AllCenteredColumn
                    sx={{
                      background: workout.isPublic
                        ? "rgba(34,212,123,0.1)"
                        : "rgba(255,255,255,0.05)",
                      border: 1,
                      borderColor: workout.isPublic
                        ? "rgba(34,212,123,0.25)"
                        : Theme.palette.dim,
                      borderRadius: 4,
                      paddingLeft: 1,
                      paddingRight: 1,
                      paddingBottom: 0.5,
                      paddingTop: 0.5,
                    }}
                  >
                    <MainText
                      sx={{
                        fontFamily: "DMSans_600SemiBold !important",
                        fontSize: "0.8rem",
                        color: workout.isPublic
                          ? Theme.palette.up + " !important"
                          : Theme.palette.muted + " !important",
                      }}
                    >
                      {workout.isPublic ? "🌐 Public" : "🔒 Private"}
                    </MainText>
                  </AllCenteredColumn>
                </Stack>
              </SpacedRow>
              <Stack>
                <Stack
                  gap={0.5}
                  mt={1}
                  height={mobileDevice ? "15vh" : "25vh"}
                  overflow={"auto"}
                  justifyContent={"center"}
                  sx={scrollBar}
                  minHeight={"150px"}
                  pr={0.2}
                >
                  {workout.structure_display && workout.type === 1 && (
                    <Box textAlign={"center"} mt={1} mb={2}>
                      <MainText
                        fontWeight={700}
                        fontSize={"1.2rem"}
                        sx={{ color: Theme.palette.orange + " !important" }}
                      >
                        {workout.structure_name}
                      </MainText>
                    </Box>
                  )}
                  {workout.type === 1 && workout.rounds > 1 && (
                    <Box textAlign={"center"} mt={1} mb={2}>
                      <MainText
                        fontWeight={700}
                        fontSize={"1.2rem"}
                        sx={{ color: Theme.palette.orange + " !important" }}
                      >
                        {workout.rounds} rounds of:
                      </MainText>
                    </Box>
                  )}
                  {workout.exercises.map((item, index) => (
                    <SpacedRow key={index} gap={1}>
                      <CenteredRow width={"30%"}>
                        {workout.type === 3 && (
                          <Box minWidth={!mobileDevice ? "50px" : "38px"}>
                            <MainText fontSize={mobileDevice && "0.8rem"}>
                              Min {index + 1}:
                            </MainText>
                          </Box>
                        )}

                        {!workout.structure_display && (
                          <Box minWidth={"90px"}>
                            <MainText
                              fontWeight={700}
                              fontSize={mobileDevice ? "0.9rem" : "1.2rem"}
                              sx={{
                                color: Theme.palette.orange + " !important",
                              }}
                            >
                              {item.reps === 5000 ? (
                                <MainTitle
                                  sx={{
                                    color: Theme.palette.purple + " !important",
                                  }}
                                >
                                  max
                                </MainTitle>
                              ) : (
                                <>
                                  {item.reps !== 0 && (
                                    <>
                                      {item.units === "m" &&
                                      settings.distance === 1 &&
                                      item.category === 3
                                        ? (item.reps * MILE_CONVERT).toFixed(2)
                                        : item.reps}{" "}
                                      {item.gender_diff === 1 &&
                                        item.exercise_name !== "run" &&
                                        item.units !== "m" &&
                                        workout.structure === 1 && (
                                          <>
                                            {item.units === "m" &&
                                            item.category === 3 &&
                                            settings.distance === 1
                                              ? "/ " +
                                                (
                                                  item.female_reps *
                                                  MILE_CONVERT
                                                ).toFixed(2)
                                              : "/ " + item.female_reps}
                                          </>
                                        )}
                                    </>
                                  )}
                                </>
                              )}
                            </MainText>
                          </Box>
                        )}
                      </CenteredRow>
                      <CenteredRow width={"500%"}>
                        <MainText fontSize={mobileDevice && "0.8rem"}>
                          {item.units !== "rep" && (
                            <>
                              {item.units === "m" &&
                              settings.distance === 1 &&
                              item.category === 3
                                ? "mi"
                                : item.units}
                            </>
                          )}{" "}
                          {item.exercise_name}
                        </MainText>
                        {item.exercise_name === "run" &&
                          item.reps !== 5000 &&
                          item.alt_reps > 0 && (
                            <MainText fontSize={"0.6rem"}>
                              (
                              {settings.distance === 0
                                ? item.alt_reps + " m"
                                : (item.alt_reps * MILE_CONVERT).toFixed(2) +
                                  " mi"}
                              )
                            </MainText>
                          )}
                        {(workout.type === 2 || workout.type === 5) &&
                          item.category === 3 &&
                          item.units === "m" && (
                            <MainText fontSize={"0.7rem"}>
                              (1 rep ={" "}
                              {settings.distance === 0 ? "25 m" : "82 ft"})
                            </MainText>
                          )}
                      </CenteredRow>
                      <Stack
                        alignItems={"end"}
                        width={mobileDevice ? "20%" : "25%"}
                        minWidth={mobileDevice ? "70px" : "90px"}
                        pr={0.2}
                        justifyContent={"center"}
                      >
                        {item.category >= 4 && (
                          <ExerciseWeight weight_info={item.weight_info} />
                        )}
                      </Stack>
                    </SpacedRow>
                  ))}
                </Stack>
                {workout.isPublic && (
                  <SpacedRow
                    p={1}
                    sx={{
                      background: Theme.palette.card,
                      border: 1,
                      borderRadius: 4,
                      borderColor: Theme.palette.borderLight,
                    }}
                  >
                    <MainText>
                      🏃 {workout.athletes_count}{" "}
                      <span
                        style={{
                          color: Theme.palette.muted,
                          fontSize: "0.7rem",
                        }}
                      >
                        athletes
                      </span>
                    </MainText>
                    {workout.type !== 3 && (
                      <>
                        {workout.best_score?.score ? (
                          <>
                            <Stack
                              direction={"row"}
                              alignItems={"center"}
                              gap={1}
                            >
                              🏆{" "}
                              <MainTitle>{workout.best_score?.score}</MainTitle>
                              <MainText
                                sx={{
                                  color: Theme.palette.muted + " !important",
                                  fontSize: "0.7rem",
                                }}
                              >
                                by {workout.best_score?.user}
                              </MainText>{" "}
                            </Stack>
                          </>
                        ) : (
                          <MainText>--:--</MainText>
                        )}
                      </>
                    )}
                  </SpacedRow>
                )}
              </Stack>
            </Stack>
          </Link>
          {workout.isPublic ? (
            <SpacedRow alignItems={"end"} mt={2} gap={4} height={"6vh"}>
              <Like
                wod_id={workout.id}
                count={workout.likes}
                isLiked={workout.isLiked}
              />
              <AllCenteredColumn
                onClick={() => {
                  setContent(<Comments workoutId={wodId} />);
                  toggleDrawer(true);
                }}
                sx={{
                  cursor: "pointer",
                  height: "100%",
                  width: "25%",
                }}
                gap={1}
              >
                <span style={{ fontSize: "1.2rem" }}>💬</span>
                <MainText sx={{ color: Theme.palette.muted + " !important" }}>
                  {workout.comment_count}
                </MainText>
              </AllCenteredColumn>
              {/* {workout.type !== 3 && (
                <AllCenteredColumn
                  sx={{
                    cursor: "pointer",
                    height: "100%",
                    width: "25%",
                  }}
                  gap={1}
                >
                  <ChallengeAthleteModal
                    workoutId={wodId}
                    isWorkoutPublic={true}
                  />

                  <CommonText
                    mobile={mobileDevice}
                    smallscreen={width < 750}
                    bold={"true"}
                  >
                    {workout.challenge_count}
                  </CommonText>
                </AllCenteredColumn>
              )} */}
              <AllCenteredColumn
                sx={{
                  cursor: "pointer",
                  height: "100%",
                  width: "25%",
                }}
                gap={1}
                onClick={() => {
                  setContent(
                    <WorkoutLeaderBoard
                      workoutId={wodId}
                      user={user}
                      drawer={true}
                    />,
                  );
                  toggleDrawer(true);
                }}
              >
                📊
                <MainText sx={{ color: Theme.palette.muted + " !important" }}>
                  Board
                </MainText>
              </AllCenteredColumn>
            </SpacedRow>
          ) : (
            <SpacedRow height={"6vh"}>
              <AllCenteredColumn
                sx={{
                  cursor: "pointer",
                  transition: "300ms",

                  "&:hover": {
                    transform: "translateY(-2px)",
                  },
                }}
                flex={1}
                onClick={handleShareWorkout}
              >
                🌐
                <SecondaryTitle>Make public</SecondaryTitle>
              </AllCenteredColumn>
              <AllCenteredColumn
                onClick={() => setConfirmModalActive(true)}
                flex={1}
                sx={{
                  borderRadius: 3,
                  cursor: "pointer",
                  transition: "300ms",

                  "&:hover": {
                    transform: "translateY(-2px)",
                  },
                }}
              >
                <span style={{ color: Theme.palette.down }}>🗑</span>
                <SecondaryTitle
                  sx={{ color: Theme.palette.down + " !important" }}
                >
                  Delete
                </SecondaryTitle>
              </AllCenteredColumn>
              <ConfirmActionModal
                active={confirmModalActive}
                setActive={setConfirmModalActive}
                action={"Delete"}
                func={handleDeleteWorkout}
                text={
                  "Are you sure you want to delete this workout? All data referring to this workout will be lost."
                }
              />
            </SpacedRow>
          )}
        </>
      )}
    </MainCard>
  );
};
export default WodDisplay;
